const CHUNK_RELOAD_KEY = "__chunk_reload_attempted__";

/**
 * Determines whether an error is a ChunkLoadError (failed lazy-loaded chunk).
 */
const isChunkLoadError = (err, message = "") => {
  const errorMessage = `${message || ""} ${err?.message || ""}`.toLowerCase();
  const errorName = (err?.name || "").toLowerCase();
  return (
    errorName === "chunkloaderror" ||
    errorMessage.includes("loading chunk") ||
    errorMessage.includes("failed to fetch dynamically imported module")
  );
};

/**
 * Determines whether an error is benign and can be safely ignored.
 */
const isBenignError = (error, message = "") => {
  const msg = `${message || ""} ${error?.message || ""}`;

  // ResizeObserver loop errors are harmless and fired frequently by UI libs
  if (/ResizeObserver loop/i.test(msg)) return true;

  // Cross-origin script errors from browser extensions (no useful info)
  if (msg === "Script error." || msg === "Script error.Script error.") return true;

  return false;
};

export function registerGlobalErrorHandlers() {
  const navigateToServerError = () => {
    // Guard against redirect loops
    if (window.location.pathname === "/Server-Error") return;
    window.location.href = "/Server-Error";
  };

  const recoverChunkError = () => {
    const hasRetried = sessionStorage.getItem(CHUNK_RELOAD_KEY) === "true";
    if (!hasRetried) {
      sessionStorage.setItem(CHUNK_RELOAD_KEY, "true");
      window.location.reload();
      return;
    }
    // Reload already failed — redirect to error page
    navigateToServerError();
  };

  // --- Synchronous JS errors ---
  window.addEventListener("error", (event) => {
    const error = event?.error;
    const message = event?.message || "";

    // ChunkLoadError: attempt recovery
    if (isChunkLoadError(error, message)) {
      event.preventDefault();
      recoverChunkError();
      return;
    }

    // Benign errors: suppress silently
    if (isBenignError(error, message)) {
      event.preventDefault();
      return;
    }

    // All other errors: log and let ErrorBoundary / React handle them.
    // Do NOT redirect — the ErrorBoundary catches React render errors,
    // and component-level try/catch handles API errors.
    console.error("[GlobalErrorHandler] Uncaught error:", error || message);
  });

  // --- Unhandled promise rejections ---
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;

    // ChunkLoadError in a dynamic import() promise: attempt recovery
    if (isChunkLoadError(reason)) {
      event.preventDefault();
      recoverChunkError();
      return;
    }

    // All other unhandled rejections: log only.
    // These are typically missed .catch() calls, not server failures.
    console.error("[GlobalErrorHandler] Unhandled promise rejection:", reason);
  });
}
